/**
 * Bluff Ratio quiz — the *bettor's-side* complement of pot odds.
 *
 * When villain bets B into a pot of P, hero needs B/(P+2B) equity to
 * profitably call. The bettor needs that *same fraction* of their
 * range to be bluffs to balance — otherwise hero can profitably fold
 * everything or call everything.
 *
 * So the math is identical to pot odds. The difference is framing:
 *   - Pot odds: "what equity do I need to call?" (caller's mental model)
 *   - Bluff ratio: "what % bluffs balances this size?" (bettor's mental model)
 *
 * Live players think about these two as separate skills even though
 * they share a formula. Keeping the quizzes separate teaches the user
 * both framings as distinct mental motions.
 */

import { pickFrom, shuffle } from "@/features/quiz/lib/common";

interface BetTier {
  fraction: number;
  cleanPots: readonly number[];
}

const BET_TIERS: readonly BetTier[] = [
  { fraction: 1 / 3, cleanPots: [12, 15, 18, 24, 30, 60, 90] },
  { fraction: 0.5, cleanPots: [10, 20, 30, 40, 50, 60, 80, 100] },
  { fraction: 2 / 3, cleanPots: [12, 15, 18, 30, 60, 90] },
  { fraction: 0.75, cleanPots: [12, 16, 20, 40, 60, 80, 100] },
  { fraction: 1.0, cleanPots: [10, 20, 30, 40, 50, 60, 80, 100] },
  { fraction: 1.5, cleanPots: [10, 20, 30, 40, 60] },
  { fraction: 2.0, cleanPots: [10, 20, 30, 40, 50, 60] },
];

/** Same canonical equity set as pot-odds quiz — bluff ratio shares
 *  the underlying math, so we share the answer surface. */
export const CANONICAL_BLUFF_PCTS = [20, 25, 29, 30, 33, 38, 40] as const;

export interface BluffRatioQuestion {
  pot: number;
  bet: number;
  betFraction: number;
  options: number[];
  correctIndex: number;
}

/** Required bluff frequency (as %) for a balanced range at the
 *  given bet size. Equals B/(P+2B) — same as pot odds. */
export function requiredBluffPct(pot: number, bet: number): number {
  return (bet / (pot + 2 * bet)) * 100;
}

function snapToCanonical(value: number): number {
  let best: number = CANONICAL_BLUFF_PCTS[0]!;
  let bestDelta = Math.abs(value - best);
  for (const v of CANONICAL_BLUFF_PCTS) {
    const delta = Math.abs(value - v);
    if (delta < bestDelta) {
      best = v;
      bestDelta = delta;
    }
  }
  return best;
}

export function generateQuestion(
  rand: () => number = Math.random,
): BluffRatioQuestion {
  const tier = pickFrom(BET_TIERS, rand);
  const pot = pickFrom(tier.cleanPots, rand);
  const bet = pot * tier.fraction;
  if (!Number.isInteger(bet)) {
    throw new Error(`Non-integer bet from tier ${tier.fraction} × ${pot}`);
  }
  const exact = requiredBluffPct(pot, bet);
  const correct = snapToCanonical(exact);

  const sorted = [...CANONICAL_BLUFF_PCTS]
    .filter((v) => v !== correct)
    .sort((a, b) => Math.abs(a - correct) - Math.abs(b - correct));
  const distractors = [sorted[0]!, sorted[1]!, sorted[sorted.length - 1]!];
  const options = shuffle([correct, ...distractors], rand);
  return {
    pot,
    bet,
    betFraction: tier.fraction,
    options,
    correctIndex: options.indexOf(correct),
  };
}

export function describeFraction(fraction: number): string {
  if (Math.abs(fraction - 1 / 3) < 0.05) return "33% pot";
  if (Math.abs(fraction - 0.5) < 0.05) return "half-pot";
  if (Math.abs(fraction - 2 / 3) < 0.05) return "two-thirds pot";
  if (Math.abs(fraction - 0.75) < 0.05) return "75% pot";
  if (Math.abs(fraction - 1.0) < 0.05) return "pot-sized";
  if (Math.abs(fraction - 1.5) < 0.1) return "150% overbet";
  if (Math.abs(fraction - 2.0) < 0.1) return "2x overbet";
  return `${Math.round(fraction * 100)}% pot`;
}
